import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getOperationsOnboardingAccountability } from '../../services/api/implementation/impl';
import {
  isOnboardingResponseForProject,
  isProgressionDataLoading,
  progressionQueryOptions
} from '../../utils/progressionQueryUtils';
import DummyTaskAccountabilityPanel from './DummyTaskAccountabilityPanel';

const DEFAULT_TAG_CLASS = 'border-blue-200 bg-blue-50 text-blue-700';

const STATUS_TAG_CLASS = {
  Completed: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  Delayed: 'border-rose-200 bg-rose-50 text-rose-700',
  Pending: 'border-amber-200 bg-amber-50 text-amber-700',
  Active: 'border-sky-200 bg-sky-50 text-sky-700',
  'Not Started': 'border-amber-200 bg-amber-50 text-amber-700',
  Locked: 'border-gray-200 bg-gray-50 text-gray-500'
};

const progressBarColor = (percent) => {
  if (percent >= 100) return 'bg-emerald-500';
  if (percent >= 50) return 'bg-amber-400';
  return 'bg-red-400';
};

const formatDueDate = (dateStr) => {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return dateStr;
  return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
};

const formatTaskStatus = (task) => {
  if (task.isDelayed) return 'Delayed';
  if (task.isLocked) return 'Locked';

  const statusMap = {
    not_started: 'Not Started',
    pending: 'Pending',
    completed: 'Completed',
    in_progress: 'Active'
  };

  return statusMap[task.status] || task.status;
};

const mapTask = (task) => {
  const status = formatTaskStatus(task);

  return {
    id: task.stepId || task.id,
    title: task.title || task.name,
    tag: task.tag || 'Operations',
    status,
    tagClassName: STATUS_TAG_CLASS[status] || DEFAULT_TAG_CLASS,
    dueDate: formatDueDate(task.dueDate),
    type: task.type
  };
};

const mapAssignmentToUser = (assignment) => ({
  id: assignment.id,
  name: assignment.name,
  role: assignment.roleLabel || assignment.designation?.name || 'Team member',
  completed: assignment.progress?.completed ?? 0,
  total: assignment.progress?.total ?? 0,
  percent: assignment.progress?.percent ?? 0,
  barColor: progressBarColor(assignment.progress?.percent ?? 0),
  tasks: (assignment.tasks || []).map(mapTask)
});

const mapAccountabilityResponse = (data, selectedOperationsUserId) => {
  const users = (data?.assignments || []).map(mapAssignmentToUser);
  const firstDelayedUser = users.find((user) =>
    user.tasks.some((task) => task.status === 'Delayed')
  );
  const selectedUserId = selectedOperationsUserId ? Number(selectedOperationsUserId) : null;
  const selectedUser = users.find((user) => user.id === selectedUserId);

  return {
    summary: {
      pending: data?.summary?.pending ?? 0,
      delayed: data?.summary?.delayed ?? 0,
      done: data?.summary?.done ?? 0,
      total: data?.summary?.total ?? 0
    },
    users,
    defaultExpandedUserId:
      selectedUser?.id ?? firstDelayedUser?.id ?? users[0]?.id ?? null
  };
};

const OperationsAccountabilityPanel = ({
  projectID = null,
  selectedOperationsUserId = null
}) => {
  const isFilterReady = projectID !== null && projectID !== undefined;
  const hasProjectSelected = isFilterReady && String(projectID) !== '';
  const resolvedProjectId = hasProjectSelected ? Number(projectID) : null;

  const {
    data: accountabilityResponse,
    isPending,
    isFetching,
    isError
  } = useQuery({
    queryKey: ['operationsOnboardingAccountability', resolvedProjectId],
    queryFn: () => getOperationsOnboardingAccountability(resolvedProjectId),
    enabled: hasProjectSelected,
    ...progressionQueryOptions
  });

  const isLoading = isProgressionDataLoading({
    isPending,
    isFetching,
    response: accountabilityResponse,
    projectId: resolvedProjectId
  });

  const panelProps = useMemo(() => {
    if (!isOnboardingResponseForProject(accountabilityResponse, resolvedProjectId)) {
      return { summary: { pending: 0, delayed: 0, done: 0, total: 0 }, users: [], defaultExpandedUserId: null };
    }

    return mapAccountabilityResponse(accountabilityResponse?.data, selectedOperationsUserId);
  }, [accountabilityResponse, resolvedProjectId, selectedOperationsUserId]);

  if (!hasProjectSelected) {
    return (
      <div className="flex h-full w-full min-w-0 flex-col rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
        <p className="py-12 text-center text-sm text-gray-500">
          Select a project to view task accountability.
        </p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex h-full w-full min-w-0 flex-col rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
        <p className="py-12 text-center text-sm text-gray-500">Loading accountability...</p>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex h-full w-full min-w-0 flex-col rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
        <p className="py-12 text-center text-sm text-rose-600">
          Unable to load accountability. Please try again.
        </p>
      </div>
    );
  }

  return (
    <DummyTaskAccountabilityPanel
      summary={panelProps.summary}
      users={panelProps.users}
      tagClassName={DEFAULT_TAG_CLASS}
      defaultExpandedUserId={panelProps.defaultExpandedUserId}
      emptyMessage="No operations onboarding assignments for this project yet."
    />
  );
};

export default OperationsAccountabilityPanel;
